import { useState } from "react";
import { observer } from "mobx-react-lite";
import { Info, X } from "lucide-react";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { parseAttachedContextMessage } from "./SidebarChatMessage";

type SlowKind = "ifc" | "dwg";

function slowKindOf(filename: string): SlowKind | null {
  const lower = filename.toLowerCase();
  if (lower.endsWith(".ifc")) return "ifc";
  if (lower.endsWith(".dwg")) return "dwg";
  return null;
}

/**
 * The slow-processing documents in messages already sent: an IFC model or a
 * DWG drawing still processing takes minutes, not seconds, and the question
 * waits for it (or goes out at the deadline) with only a spinning chip to show.
 */
export function slowSentAttachments(
  messages: ReadonlyArray<{ role: string; content: string }>,
  loadingDocIds: ReadonlySet<string>
): Array<{ id: string; kind: SlowKind }> {
  const found = new Map<string, SlowKind>();
  for (const message of messages) {
    if (message.role !== "user") continue;
    const parsed = parseAttachedContextMessage(message.content);
    for (const doc of parsed?.attachedDocuments ?? []) {
      const kind = slowKindOf(doc.filename);
      if (kind && loadingDocIds.has(doc.id)) found.set(doc.id, kind);
    }
  }
  return Array.from(found, ([id, kind]) => ({ id, kind }));
}

/**
 * A closable line above the composer while a sent message's IFC or DWG is
 * still processing. Closing it hides it for these documents, even as some of
 * them finish; a later send with another one shows it again.
 */
export const AttachmentProcessingNote = observer(function AttachmentProcessingNote({
  messages,
  loadingDocIds,
}: {
  messages: ReadonlyArray<{ role: string; content: string }>;
  /** Observed: pass `chatStore.loadingDocIds` itself, not a copy. */
  loadingDocIds: ReadonlySet<string>;
}) {
  const { t } = useLanguage();
  const copy = t.documents.chatSidebar.slowProcessingNote;
  // The documents the user closed the note for. Keyed by document, not by
  // the set: one of two finishing must not bring back a note already closed.
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(() => new Set());

  const slow = slowSentAttachments(messages, loadingDocIds);
  if (slow.every((d) => dismissed.has(d.id))) return null;

  const kinds = new Set(slow.map((d) => d.kind));
  const text = kinds.size > 1 ? copy.both : kinds.has("ifc") ? copy.ifc : copy.dwg;

  return (
    <div
      role="status"
      data-testid="attachment-processing-note"
      className="mx-3 mb-1.5 flex items-start gap-1.5 rounded-md bg-neutral-100 px-2 py-1.5 text-[11px] leading-4 text-neutral-600"
    >
      <Info className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
      <p className="min-w-0 flex-1">{text}</p>
      <button
        type="button"
        onClick={() => setDismissed(new Set([...dismissed, ...slow.map((d) => d.id)]))}
        aria-label={copy.dismiss}
        className="shrink-0 rounded p-0.5 text-neutral-400 hover:bg-neutral-200 hover:text-neutral-700"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
});
